/**
 * `AppTopBar` — the 50px product-window top bar shown for every module except
 * the chromeless Mobil / Meeting screens. A dark logo square, a row of pill nav
 * buttons (Inbox carries a verdigris count badge), and a right cluster with a
 * "Suchen" field, a ⌘K kbd chip, and the user avatar. Clicking a nav pill
 * switches the active module via `setActive`. 1:1 with the design map §3.
 */
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import { Search } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

import { BRAND_LOGO_ALT, BRAND_LOGO_TITLE } from "@/lib/marketing-seo";
import { Avatar } from "../hero-primitives-app";
import { H } from "../hero-tokens";
import type { HeroModuleKey } from "../hero-types";

/**
 * Nav pills. `key` is the module a pill switches to; `null` = inert ("Mehr").
 * "Stammdaten" routes to the `kontakte` sub-view (the Stammdaten default).
 * `labelKey` is both the message key and the pill's stable identity.
 */
interface NavPill {
  readonly labelKey: string;
  readonly key: HeroModuleKey | null;
  readonly badge?: string;
}

const NAV_PILLS: readonly NavPill[] = [
  { labelKey: "inbox", key: "inbox", badge: "3" },
  { labelKey: "dokumente", key: "dokumente" },
  { labelKey: "telefon", key: "telefon" },
  { labelKey: "teamChat", key: "teamchat" },
  { labelKey: "stammdaten", key: "kontakte" },
  { labelKey: "aufgaben", key: "aufgaben" },
  { labelKey: "agents", key: "agents" },
  { labelKey: "mehr", key: null },
];

/** True when `pill` represents the currently-active module. */
function isPillActive(pill: NavPill, active: HeroModuleKey): boolean {
  if (pill.labelKey === "mehr") {
    // "Mehr" (overflow menu) lights up for the modules that live under it
    // (design line 2578).
    return (
      active === "wissen" ||
      active === "kalender" ||
      active === "kopilot" ||
      active === "formulare"
    );
  }
  if (pill.key === null) return false;
  if (pill.labelKey === "stammdaten") {
    // The Stammdaten pill stays lit across all its sub-views.
    return (
      active === "kontakte" ||
      active === "liegenschaften" ||
      active === "portal" ||
      active === "serienbrief" ||
      active === "etv"
    );
  }
  if (pill.labelKey === "dokumente") {
    return active === "dokumente" || active === "posteingangsscan";
  }
  return pill.key === active;
}

function NavButton(props: {
  readonly pill: NavPill;
  readonly active: boolean;
  readonly onClick?: () => void;
}): ReactNode {
  const t = useTranslations("marketing.appTopBar");
  const { pill, active } = props;
  return (
    <button
      type="button"
      onClick={props.onClick}
      style={{
        appearance: "none",
        border: "none",
        cursor: pill.key === null ? "default" : "pointer",
        height: 33,
        padding: "0 12px",
        borderRadius: 9,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: H.sans,
        fontSize: 13,
        fontWeight: 600,
        background: active ? "#fff" : "transparent",
        color: active ? H.ink : "rgba(11,15,25,0.5)",
        boxShadow: active ? "0 1px 2px rgba(11,15,25,0.10)" : "none",
      }}
    >
      {t(pill.labelKey)}
      {pill.badge !== undefined ? (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 17,
            height: 17,
            padding: "0 4px",
            borderRadius: 9,
            background: H.accent,
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          {pill.badge}
        </span>
      ) : null}
    </button>
  );
}

export function AppTopBar(props: {
  readonly active: HeroModuleKey;
  readonly setActive: (key: HeroModuleKey) => void;
}): ReactNode {
  const t = useTranslations("marketing.appTopBar");
  return (
    <div
      style={{
        height: 50,
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "0 14px",
        borderBottom: "1px solid rgba(11,15,25,0.03)",
        background: "rgba(252,252,251,0.08)",
        backdropFilter: "blur(6px) saturate(1.15)",
        WebkitBackdropFilter: "blur(6px) saturate(1.15)",
      }}
    >
      {/* Brand mark (no wordmark) — same asset the app loads. */}
      <span
        role="img"
        aria-label={BRAND_LOGO_ALT}
        title={BRAND_LOGO_TITLE}
        style={{ display: "inline-flex", flexShrink: 0 }}
      >
        <BrandLogoMark width={28} height={28} variant="dark" />
      </span>

      {/* Nav pills */}
      <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
        {NAV_PILLS.map((pill) => (
          <NavButton
            key={pill.labelKey}
            pill={pill}
            active={isPillActive(pill, props.active)}
            onClick={
              pill.key === null
                ? undefined
                : () => {
                    if (pill.key !== null) props.setActive(pill.key);
                  }
            }
          />
        ))}
      </div>

      {/* Right cluster — search field + ⌘K + avatar */}
      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            height: 33,
            padding: "0 11px",
            borderRadius: 9,
            border: "1px solid rgba(11,15,25,0.10)",
            background: "#fff",
            color: H.inkDim,
            fontSize: 13,
            minWidth: 100,
          }}
        >
          <Search size={15} strokeWidth={1.8} />
          <span>{t("suchen")}</span>
          <span
            style={{
              marginLeft: "auto",
              fontFamily: H.label,
              fontSize: 10.5,
              background: "#fff",
              border: "1px solid #E7E9ED",
              borderRadius: 5,
              padding: "1px 5px",
            }}
          >
            {t("k")}
          </span>
        </div>
        <Avatar initials="NH" size={28} />
      </div>
    </div>
  );
}
